import { type MergedCoupon } from '@cart-services/types'
import { type CountryCode, type ProjectCode } from '@constants'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { type ECommerceProduct, type Variation } from '@hooks-api/types/dataModel/products'
import { type ProductCategoryEvent, type PromotionPageEvent } from '../types/fastpass-merchant-events'
import { useAnalytics } from './useAnalytics'

interface UseFastpassMerchantGoogleAnalyticEventProps {
  projectCode: ProjectCode
  countryCode: CountryCode
  campaignId: number
}

const utilsConvertCouponToEventParam = (coupon: MergedCoupon) => {
  return {
    coupon_id: coupon.coupon_id,
    coupon_title: coupon.display_name,
    coupon_threshold: coupon.criterions.map(criterion => criterion.display_name).join(','), // threshold 可能有多項，用逗號連接成單一字串
  }
}

export const useFastpassMerchantGoogleAnalyticEvent = (props: UseFastpassMerchantGoogleAnalyticEventProps) => {
  const { projectCode, countryCode, campaignId } = props
  const { sendEvent } = useAnalytics()

  const sendClickProductPictureEvent = useCallbackEvent(() => {
    sendEvent({
      eventName: 'fastpass_merchant_click_product_picture',
      eventParams: {
        event_category: 'product',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })

  const sendClickProductNameEvent = useCallbackEvent(() => {
    sendEvent({
      eventName: 'fastpass_merchant_click_product_name',
      eventParams: {
        event_category: 'product',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })

  const sendClickOrderTotalEvent = useCallbackEvent(() => {
    sendEvent({
      eventName: 'fastpass_merchant_click_order_total',
      eventParams: {
        event_category: 'product',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })

  const sendClickGiftIconEvent = useCallbackEvent(() => {
    sendEvent({
      eventName: 'fastpass_merchant_click_gift_icon',
      eventParams: {
        event_category: 'product',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })

  const sendBeginCartEvent = useCallbackEvent(() => {
    sendEvent({
      eventName: 'fastpass_merchant_begin_cart',
      eventParams: {
        event_category: 'product',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })

  const sendClickSearchBarEvent = useCallbackEvent(() => {
    sendEvent({
      eventName: 'fastpass_merchant_click_search_bar',
      eventParams: {
        event_category: 'search',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })

  const sendSearchKeywordEvent = useCallbackEvent((keyword: string) => {
    sendEvent({
      eventName: 'fastpass_merchant_search_keyword',
      eventParams: {
        event_category: 'search',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
        search_keyword: keyword,
      },
    })
  })

  const sendSearchResultClickEvent = useCallbackEvent((product: Variation) => {
    sendEvent({
      eventName: 'fastpass_merchant_click_search_result',
      eventParams: {
        event_category: 'search',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
        product_sku: product.sku,
        product_real_sku: product.real_sku ?? '',
        variation_id: product.variation_id,
        product_name: product.name,
      },
    })
  })

  const sendPastResultClickEvent = useCallbackEvent((keyword: string) => {
    sendEvent({
      eventName: 'fastpass_merchant_click_past_result',
      eventParams: {
        event_category: 'search',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
        past_keyword: keyword,
      },
    })
  })

  const sendRemoveProductEvent = useCallbackEvent((product: ECommerceProduct) => {
    if (!product.sku || !product.real_sku || !product.product_id) {
      return
    }
    const price = Number(product.sale_price) // EcommerceProduct 中型別為 string | number，需要轉換成 number

    sendEvent({
      eventName: 'fastpass_merchant_remove_product',
      eventParams: {
        event_category: 'cart',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
        product_sku: product.sku,
        product_real_sku: product.real_sku,
        variation_id: product.product_id,
        product_name: product.title,
        product_price: price,
      },
    })
  })

  const sendClickCouponEvent = useCallbackEvent(() => {
    sendEvent({
      eventName: 'fastpass_merchant_click_coupon',
      eventParams: {
        event_category: 'coupon',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })

  const sendClickCouponTabEvent = useCallbackEvent((tab: 'auto_coupon' | 'manual_coupon') => {
    sendEvent({
      eventName: 'fastpass_merchant_click_coupon_tab',
      eventParams: {
        event_category: 'coupon',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
        coupon_tab: tab,
      },
    })
  })

  const sendApplyCouponEvent = useCallbackEvent((coupon: MergedCoupon) => {
    sendEvent({
      eventName: 'fastpass_merchant_apply_coupon_code',
      eventParams: {
        event_category: 'coupon',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
        ...utilsConvertCouponToEventParam(coupon),
      },
    })
  })

  const sendLeaveCouponEvent = useCallbackEvent(() => {
    sendEvent({
      eventName: 'fastpass_merchant_leave_coupon',
      eventParams: {
        event_category: 'coupon',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })

  const sendClickMyAccountIconEvent = useCallbackEvent(() => {
    sendEvent({
      eventName: 'fastpass_merchant_click_my_account_icon',
      eventParams: {
        event_category: 'my_account',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })

  const sendCancelOrderEvent = useCallbackEvent(() => {
    sendEvent({
      eventName: 'fastpass_merchant_order_canceled',
      eventParams: {
        event_category: 'my_account',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })

  const sendBeginRepayEvent = useCallbackEvent(() => {
    sendEvent({
      eventName: 'fastpass_merchant_begin_repay',
      eventParams: {
        event_category: 'my_account',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })

  const sendRepayOrderEvent = useCallbackEvent(() => {
    sendEvent({
      eventName: 'fastpass_merchant_repay_order',
      eventParams: {
        event_category: 'repay',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })

  const sendPromotionPageEvent = useCallbackEvent((eventName: PromotionPageEvent['eventName']) => {
    sendEvent({
      eventName,
      eventParams: {
        event_category: 'promotion_page',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })

  const sendExpandCouponParentProductEvent = useCallbackEvent((coupon: MergedCoupon) => {
    sendEvent({
      eventName: 'fastpass_merchant_expand_parent_product',
      eventParams: {
        event_category: 'promotion_card',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
        coupon_tab: 'manual_coupon',
        ...utilsConvertCouponToEventParam(coupon),
      },
    })
  })
  const sendExpandCouponChildProductEvent = useCallbackEvent((variations: ECommerceProduct[]) => {
    const _variations = variations.map((variation) => {
      return {
        sku: variation.sku || '',
        real_sku: variation.real_sku || '',
        id: String(variation.id),
        name: variation.name || '',
        price: Number(variation.sale_price),
      }
    })
    sendEvent({
      eventName: 'fastpass_merchant_expand_child_product',
      eventParams: {
        event_category: 'promotion_card',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
        variations: _variations || [],
      },
    })
  })

  const sendOpenPromotionPopupEvent = useCallbackEvent(() => {
    sendEvent({
      eventName: 'fastpass_merchant_open_promotion_popup',
      eventParams: {
        event_category: 'promotion_popup',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })

  const sendGoToPromotionPageEvent = useCallbackEvent((coupon: MergedCoupon) => {
    sendEvent({
      eventName: 'fastpass_merchant_go_to_promotion_page',
      eventParams: {
        event_category: 'promotion_popup',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
        coupon_tab: 'manual_coupon',
        ...utilsConvertCouponToEventParam(coupon),
      },
    })
  })
  const sendProductCategoryEvent = useCallbackEvent((eventName: ProductCategoryEvent['eventName']) => {
    sendEvent({
      eventName,
      eventParams: {
        event_category: 'product_categories',
        project_code: projectCode,
        country_code: countryCode,
        campaign_code: campaignId,
      },
    })
  })
  return {
    sendProductCategoryEvent,
    sendGoToPromotionPageEvent,
    sendOpenPromotionPopupEvent,
    sendExpandCouponChildProductEvent,
    sendPromotionPageEvent,
    sendExpandCouponParentProductEvent,
    sendClickProductPictureEvent,
    sendClickProductNameEvent,
    sendClickOrderTotalEvent,
    sendClickGiftIconEvent,
    sendBeginCartEvent,
    sendClickSearchBarEvent,
    sendSearchKeywordEvent,
    sendSearchResultClickEvent,
    sendPastResultClickEvent,
    sendRemoveProductEvent,
    sendClickCouponEvent,
    sendClickCouponTabEvent,
    sendApplyCouponEvent,
    sendLeaveCouponEvent,
    sendClickMyAccountIconEvent,
    sendCancelOrderEvent,
    sendBeginRepayEvent,
    sendRepayOrderEvent,
  }
}
